import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { virtualDroneService } from '../../services/virtualDroneService';
import { VirtualDrone, virtualDroneName } from '../../types/virtualDrone';
import { MAX_DRONE_NAME_LENGTH } from '../../types/drone';
import { ApiError } from '../../types/api';

/** Loud, operator-facing message for the failure modes this flow can hit. */
const describeError = (err: unknown, fallback: string): string => {
  const status = (err as ApiError)?.status;
  switch (status) {
    case 401:
      return 'Your session has expired — sign in again.';
    case 403:
      return 'You do not have permission to manage virtual drones.';
    case 409:
      return 'You have reached the limit of virtual drones. Delete one before adding another.';
    case 501:
      // The cluster has no simulator configured (#526). Prefer the server's
      // own message: it names the setting an operator has to change, and a
      // generic "not implemented" would send them looking for a bug instead.
      // Distinct from 502 on purpose — retrying will never help here.
      return (
        (err as ApiError)?.serverMessage ??
        'Virtual drones are not available on this cluster: no simulator is configured.'
      );
    case 502:
      return 'The simulator could not be started. Try again shortly.';
    default:
      return `${fallback}: ${(err as ApiError)?.message ?? String(err)}`;
  }
};

interface VirtualDroneDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Add, see and delete operator-created virtual (simulated) drones (#322). The
 * drone also appears on the fleet map through the normal drone feed once its
 * simulator reports.
 *
 * This used to own a floating action button of its own, in the opposite corner
 * from the one that enrols real hardware — the two read as one modern control and
 * one legacy one, which is #525. There is now a single "Register drone" entry
 * point that asks which kind first (#389), so this is a dialog and nothing else.
 */
export const VirtualDroneDialog: React.FC<VirtualDroneDialogProps> = ({ open, onClose }) => {
  const [drones, setDrones] = useState<VirtualDrone[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const { drones: list } = await virtualDroneService.list();
      setDrones(list);
    } catch (err) {
      setError(describeError(err, 'Could not load virtual drones'));
      setDrones([]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setDrones(null);
      void refresh();
    }
  }, [open, refresh]);

  const onAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      await virtualDroneService.create(name);
      setName('');
      await refresh();
    } catch (err) {
      setError(describeError(err, 'Could not add virtual drone'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await virtualDroneService.remove(id);
      await refresh();
    } catch (err) {
      setError(describeError(err, 'Could not delete virtual drone'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Virtual drones</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Name (optional)"
            value={name}
            inputProps={{ maxLength: MAX_DRONE_NAME_LENGTH }}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) void onAdd();
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onAdd}
            disabled={busy}
          >
            Add
          </Button>
        </Box>

        {drones === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : drones.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No virtual drones yet. Add one to test missions without hardware.
          </Typography>
        ) : (
          <List>
            {drones.map((d) => (
              <ListItem
                key={d.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Delete ${virtualDroneName(d)}`}
                    onClick={() => onDelete(d.id)}
                    disabled={busy}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={virtualDroneName(d)}
                  secondary={d.id}
                />
                <Chip size="small" label={d.status} sx={{ mr: 6 }} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
